import { useState, useEffect } from 'react';
import { X, Plus, Calendar, FileText, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { patientsApi, Patient } from '../../lib/api';

interface MedicalRecord {
  id: string;
  patientId: string;
  condition: string;
  diagnosedDate: string;
  status: 'ACTIVE' | 'RESOLVED' | 'CHRONIC';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface AddMedicalRecordRequest {
  condition: string;
  diagnosedDate: string;
  status: 'ACTIVE' | 'RESOLVED' | 'CHRONIC';
  notes?: string;
}

interface MedicalHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient | null;
}

const MedicalHistoryModal = ({ isOpen, onClose, patient }: MedicalHistoryModalProps) => {
  const [medicalHistory, setMedicalHistory] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);

  const [newRecord, setNewRecord] = useState<AddMedicalRecordRequest>({
    condition: '',
    diagnosedDate: new Date().toISOString().split('T')[0],
    status: 'ACTIVE',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && patient) {
      fetchMedicalHistory();
    }
  }, [isOpen, patient]);

  const fetchMedicalHistory = async () => {
    if (!patient) return;

    setLoading(true);
    setError(null);
    try {
      const response = await patientsApi.getPatientMedicalHistory(patient.id);
      if (response.data.success) {
        setMedicalHistory(response.data.data || []);
      } else {
        setError('Failed to load medical history');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load medical history');
      setMedicalHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async () => {
    if (!patient || !newRecord.condition.trim()) {
      toast.error('Please fill in required fields');
      return;
    }

    setAddingRecord(true);
    try {
      const response = await patientsApi.addPatientMedicalHistory(patient.id, newRecord);
      if (response.data.success) {
        toast.success('Medical record added successfully');
        setMedicalHistory(prev => [response.data.data, ...prev]);
        setShowAddForm(false);
        resetForm();
      } else {
        toast.error('Failed to add medical record');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add medical record');
    } finally {
      setAddingRecord(false);
    }
  };

  const resetForm = () => {
    setNewRecord({
      condition: '',
      diagnosedDate: new Date().toISOString().split('T')[0],
      status: 'ACTIVE',
      notes: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'text-blue-600 bg-blue-100';
      case 'RESOLVED': return 'text-green-600 bg-green-100';
      case 'CHRONIC': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE': return AlertTriangle;
      case 'RESOLVED': return Plus;
      case 'CHRONIC': return Calendar;
      default: return FileText;
    }
  };

  if (!isOpen || !patient) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-primary-50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Medical History</h2>
              <p className="text-primary-600 font-medium">
                {patient.user.firstName} {patient.user.lastName} (#{patient.patientNumber})
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Record
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Add Record Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Medical Record</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Diagnosed Date *</label>
                  <input
                    type="date"
                    className="input"
                    value={newRecord.diagnosedDate}
                    onChange={(e) => setNewRecord(prev => ({ ...prev, diagnosedDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Status *</label>
                  <select
                    className="input"
                    value={newRecord.status}
                    onChange={(e) => setNewRecord(prev => ({ ...prev, status: e.target.value as any }))}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CHRONIC">Chronic</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="form-label">Condition *</label>
                <input
                  type="text"
                  className="input"
                  value={newRecord.condition}
                  onChange={(e) => setNewRecord(prev => ({ ...prev, condition: e.target.value }))}
                  placeholder="Enter medical condition"
                />
              </div>

              <div className="mt-4">
                <label className="form-label">Notes</label>
                <textarea
                  className="input min-h-[100px]"
                  value={newRecord.notes || ''}
                  onChange={(e) => setNewRecord(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Enter additional notes about the condition"
                />
              </div>

              <div className="flex items-center justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                  disabled={addingRecord}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRecord}
                  className="btn-primary"
                  disabled={addingRecord}
                >
                  {addingRecord ? 'Adding...' : 'Add Record'}
                </button>
              </div>
            </div>
          )}

          {/* Medical History List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading medical history...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">Error loading medical history</div>
              <p className="text-gray-600">{error}</p>
              <button 
                onClick={fetchMedicalHistory}
                className="mt-4 btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : medicalHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <div className="text-gray-500 mb-2">No medical history found</div>
              <p className="text-gray-400">Patient has no medical records yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {medicalHistory.map((record) => {
                const StatusIcon = getStatusIcon(record.status);
                return (
                  <div key={record.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`p-2 rounded-lg ${getStatusColor(record.status)}`}>
                          <StatusIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{record.condition}</h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record.status)}`}>
                              {record.status}
                            </span>
                          </div>
                          
                          {record.notes && (
                            <p className="text-gray-600 mb-3">{record.notes}</p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              Diagnosed: {new Date(record.diagnosedDate).toLocaleDateString()}
                            </div>
                            <div>
                              Added: {new Date(record.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicalHistoryModal; 